<template>
  <div class="spot-detail-page">
    <!-- 顶部栏 -->
    <div class="top-bar">
      <el-button icon="el-icon-arrow-left" size="small" @click="goBack">返回</el-button>
      <span class="spot-title">{{ spotInfo.name || '图斑详情' }}</span>
      <div class="stage-controls">
        <el-select v-model="currentStageId" placeholder="选择阶段" style="width: 260px;" @change="handleStageChange">
          <el-option v-for="item in stageList" :key="item.id" :label="item.label" :value="item.id" />
        </el-select>
        <el-button v-if="canManage && currentStage && currentStage.status === 0" type="warning" size="small" style="margin-left: 8px;" @click="handleFinish">设为已完结</el-button>
        <el-button v-if="canManage && currentStage && currentStage.status === 1" type="info" size="small" style="margin-left: 8px;" @click="handleUnfinish">设为未完结</el-button>
        <el-button v-if="canManage" type="primary" size="small" style="margin-left: 8px;" @click="openAddStage">添加阶段</el-button>
      </div>
    </div>

    <!-- 左侧悬浮菜单 -->
    <div class="left-menu">
      <div class="menu-item" :class="{ active: leftActive === 'map' }" @click="goHome">
        <i class="el-icon-map-location"></i>
        <div class="menu-label">一张图</div>
      </div>
      <div class="menu-item" @click="openSpotInfo">
        <i class="el-icon-document"></i>
        <div class="menu-label">图斑信息</div>
      </div>
      <div class="menu-item" @click="goCompare">
        <i class="el-icon-copy-document"></i>
        <div class="menu-label">同屏对比</div>
      </div>
      <div class="menu-item" @click="openMapUpload">
        <i class="el-icon-upload"></i>
        <div class="menu-label">上传地图</div>
      </div>
      <div class="menu-item" @click="goMonitorTask">
        <i class="el-icon-s-operation"></i>
        <div class="menu-label">监测任务</div>
      </div>
    </div>

    <!-- 地图容器 -->
    <div class="map-container">
      <swqyun-map
        ref="swqyunMap"
        style="width: 100%; height: 100%;"
        :scene-url="displaySceneUrl"
        :session-key="displaySessionKey"
        :app-id="displayAppId"
        :editable="false"
        :vectors="mapVectors"
        :auto-fly="true"
        scene="spot"
      />
    </div>

    <!-- 底部悬浮柱状图 -->
    <div class="bottom-chart-panel" v-if="statisticsData.length > 0">
      <div class="chart-title">历年汇总数据</div>
      <div ref="statisticsChart" class="statistics-chart"></div>
    </div>

    <!-- 图斑信息弹窗 -->
    <spot-stage-info :visible.sync="infoVisible" :spot-id="spotId" :stage-list="stageList" />

    <!-- 上传地图影像弹窗 -->
    <spot-stage-map-upload :visible.sync="mapUploadVisible" :stage-id="currentStageId" @success="handleStageChange" />

    <!-- 添加阶段弹窗 -->
    <el-dialog title="添加阶段" :visible.sync="addStageVisible" width="400px">
      <el-form :model="stageForm" label-width="80px">
        <el-form-item label="年份">
          <el-input-number v-model="stageForm.year" :min="2000" :max="2100" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="阶段">
          <el-input-number v-model="stageForm.stageOrder" :min="1" :max="100" style="width: 100%;" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="addStageVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmAddStage">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import SwqyunMap from "@/components/SwqyunMap.vue";
import SpotStageInfo from "./components/spotStageInfo.vue";
import SpotStageMapUpload from "./components/spotStageMapUpload.vue";
// echarts is loaded globally

export default {
  components: { SwqyunMap, SpotStageInfo, SpotStageMapUpload },
  data() {
    return {
      spotId: null,
      spotInfo: {},
      sceneUrl: "",
      sessionKey: "",
      appId: "",
      stageList: [],
      currentStageId: null,
      currentStage: null,
      mapVectors: [],
      spotVectors: [],
      stageVectors: [],
      overlaySceneUrl: "",
      overlaySessionKey: "",
      overlayAppId: "",
      statisticsData: [],
      leftActive: "map",
      infoVisible: false,
      mapUploadVisible: false,
      addStageVisible: false,
      stageForm: { year: new Date().getFullYear(), stageOrder: 1 },
      canManage: false
    };
  },
  created() {
    this.spotId = this.$route.query.spotId ? Number(this.$route.query.spotId) : null;
    if (!this.spotId) {
      this.$message.error("缺少图斑ID");
      this.$router.push("/");
      return;
    }
    this.initUserRole();
    this.loadSpotInfo();
    this.loadMapConfig();
    this.loadStageList();
    this.loadStatistics();
  },
  computed: {
    displaySceneUrl() {
      return this.overlaySceneUrl || this.sceneUrl;
    },
    displaySessionKey() {
      return this.overlaySceneUrl ? this.overlaySessionKey : this.sessionKey;
    },
    displayAppId() {
      return this.overlaySceneUrl ? this.overlayAppId : this.appId;
    }
  },
  methods: {
    initUserRole() {
      try {
        const infoStr = localStorage.getItem("adminUserInfo");
        if (infoStr) {
          const info = JSON.parse(infoStr);
          this.canManage = info.role === "SUPER_ADMIN" || info.role === "ADMIN";
        }
      } catch (e) {
        this.canManage = false;
      }
    },
    loadSpotInfo() {
      this.$apis.get("/admin/spot/search", { fieldNo: "" }).then(res => {
        // 临时用列表接口取图斑详情，实际应加/detail接口
      });
      // 先通过 spotManage 的 page 接口过滤
      this.$apis.get("/admin/spot/page", { pageIndex: 1, pageSize: 1000 }).then(res => {
        if (res.data.code == 200 && res.data.data && res.data.data.list) {
          const spot = res.data.data.list.find(item => item.id === this.spotId);
          if (spot) {
            this.spotInfo = spot;
            // 解析图斑本身的矢量范围
            if (spot.vectorGeoJson) {
              const parsed = this.parseVectorGeoJson(spot.vectorGeoJson);
              this.spotVectors = this.toMapVectors(parsed);
            } else {
              this.spotVectors = [];
            }
            this.refreshMapVectors();
          }
        }
      });
    },
    refreshMapVectors() {
      this.mapVectors = [...this.spotVectors, ...this.stageVectors];
    },
    loadMapConfig() {
      const districtId = this.$store.state.district && this.$store.state.district.districtInfo && this.$store.state.district.districtInfo.districtId;
      this.$apis.get("/admin/districtMapConfig/detail", { districtId }).then(res => {
        if (res && res.data && res.data.code == 200 && res.data.data) {
          const cfg = res.data.data;
          this.sceneUrl = cfg.sceneUrl;
          this.sessionKey = cfg.sessionKey;
          this.appId = cfg.appId;
        } else {
          this.sceneUrl = "https://api.swqyun.com/api/fileManage/Scene/SDKGetScene?id=138b92545134d878d5d90ca45490263c";
          this.sessionKey = "0271ae43f09e427cb6927d6255446bc0b3cec903f45b48a49fb22dba752f52cb";
          this.appId = "31f37381eb434c2b";
        }
      }).catch(() => {
        this.sceneUrl = "https://api.swqyun.com/api/fileManage/Scene/SDKGetScene?id=138b92545134d878d5d90ca45490263c";
        this.sessionKey = "0271ae43f09e427cb6927d6255446bc0b3cec903f45b48a49fb22dba752f52cb";
        this.appId = "31f37381eb434c2b";
      });
    },
    loadStageList() {
      this.$apis.get("/admin/spot/stage/list", { spotId: this.spotId }).then(res => {
        if (res.data.code == 200) {
          this.stageList = res.data.data || [];
          if (this.stageList.length > 0) {
            this.currentStageId = this.stageList[0].id;
            this.currentStage = this.stageList[0];
            this.loadStageDetail();
          }
        }
      });
    },
    handleStageChange() {
      this.currentStage = this.stageList.find(item => item.id === this.currentStageId) || null;
      this.loadStageDetail();
    },
    loadStageDetail() {
      if (!this.currentStageId) return;
      this.$apis.get("/admin/spot/stage/detail", { stageId: this.currentStageId }).then(res => {
        if (res.data.code == 200 && res.data.data) {
          const detail = res.data.data;
          // 构建阶段矢量数据
          const vectors = [];
          // 解析 GeoJSON 矢量
          if (detail.vectorGeoJson) {
            const parsed = this.parseVectorGeoJson(detail.vectorGeoJson);
            const mapVecs = this.toMapVectors(parsed);
            vectors.push(...mapVecs);
          }
          // 资源点也转为矢量
          const resources = detail.resources || [];
          resources.forEach(r => {
            if (r.longitude != null && r.latitude != null) {
              vectors.push({
                calcType: "point",
                pointList: [{
                  longitude: String(r.longitude),
                  latitude: String(r.latitude),
                  height: "0",
                  imageList: []
                }]
              });
            }
          });
          this.stageVectors = vectors;
          this.refreshMapVectors();
          // 解析阶段2D地图配置
          this.overlaySceneUrl = detail.map2dSceneUrl || "";
          this.overlaySessionKey = detail.map2dSessionKey || "";
          this.overlayAppId = detail.map2dAppId || "";
        }
      });
    },
    parseVectorGeoJson(geoJsonStr) {
      if (!geoJsonStr) return [];
      try {
        const geo = JSON.parse(geoJsonStr);
        if (!geo) return [];
        // 兼容前端原始格式 {calcType, pointList}
        if (geo.calcType && Array.isArray(geo.pointList)) {
          const type = geo.calcType === "point" ? "point" : (geo.calcType === "line" ? "line" : "area");
          if (type === "point" && geo.pointList.length > 0) {
            return [{ id: "sv_0", type: "point", coordinates: [parseFloat(geo.pointList[0].longitude), parseFloat(geo.pointList[0].latitude)] }];
          } else if (type === "line") {
            return [{ id: "sv_0", type: "line", coordinates: geo.pointList.map(p => [parseFloat(p.longitude), parseFloat(p.latitude)]) }];
          } else if (type === "area") {
            return [{ id: "sv_0", type: "area", coordinates: geo.pointList.map(p => [parseFloat(p.longitude), parseFloat(p.latitude)]), area: 0 }];
          }
          return [];
        }
        // 标准 GeoJSON FeatureCollection
        if (!geo.features) return [];
        return geo.features.map((f, idx) => {
          const type = f.geometry.type;
          if (type === "Point") {
            return { id: (f.properties && f.properties.id) || "p" + idx, type: "point", coordinates: f.geometry.coordinates };
          } else if (type === "LineString") {
            return { id: (f.properties && f.properties.id) || "l" + idx, type: "line", coordinates: f.geometry.coordinates };
          } else if (type === "Polygon") {
            return { id: (f.properties && f.properties.id) || "a" + idx, type: "area", coordinates: f.geometry.coordinates[0], area: (f.properties && f.properties.area) || 0 };
          }
          return null;
        }).filter(Boolean);
      } catch (e) {
        return [];
      }
    },
    toMapVectors(vectors) {
      if (!vectors || vectors.length === 0) return [];
      return vectors.map(v => {
        if (v.type === "point") {
          return {
            calcType: "point",
            pointList: [{ latitude: v.coordinates[1], longitude: v.coordinates[0], height: 0, imageList: [] }]
          };
        } else if (v.type === "line") {
          return {
            calcType: "line",
            pointList: v.coordinates.map(c => ({ latitude: c[1], longitude: c[0], height: 0, imageList: [] }))
          };
        } else if (v.type === "area") {
          return {
            calcType: "area",
            pointList: v.coordinates.map(c => ({ latitude: c[1], longitude: c[0], height: 0, imageList: [] }))
          };
        }
        return null;
      }).filter(Boolean);
    },
    handleFinish() {
      this.$confirm("确认将该阶段设为已完结？", "提示", { type: "warning" }).then(() => {
        this.$apis.post("/admin/spot/stage/finish", { id: this.currentStageId }).then(res => {
          if (res.data.code == 200) {
            this.$message.success("操作成功");
            this.loadStageList();
          } else {
            this.$message.error(res.data.msg || res.data.message || "操作失败");
          }
        });
      });
    },
    handleUnfinish() {
      this.$confirm("确认将该阶段设为未完结？", "提示", { type: "warning" }).then(() => {
        this.$apis.post("/admin/spot/stage/unfinish", { id: this.currentStageId }).then(res => {
          if (res.data.code == 200) {
            this.$message.success("操作成功");
            this.loadStageList();
          } else {
            this.$message.error(res.data.msg || res.data.message || "操作失败");
          }
        });
      });
    },
    openAddStage() {
      this.stageForm = { year: new Date().getFullYear(), stageOrder: 1 };
      this.addStageVisible = true;
    },
    confirmAddStage() {
      this.$apis.post("/admin/spot/stage/add", {
        spotId: this.spotId,
        year: this.stageForm.year,
        stageOrder: this.stageForm.stageOrder
      }).then(res => {
        if (res.data.code == 200) {
          this.$message.success("添加成功");
          this.addStageVisible = false;
          this.loadStageList();
        } else {
          this.$message.error(res.data.msg || res.data.message || "添加失败");
        }
      });
    },
    loadStatistics() {
      this.$apis.get("/admin/spot/stage/statistics", { spotId: this.spotId }).then(res => {
        if (res.data.code == 200) {
          this.statisticsData = res.data.data || [];
          this.$nextTick(() => this.renderChart());
        }
      });
    },
    renderChart() {
      if (!this.$refs.statisticsChart || this.statisticsData.length === 0) return;
      const chart = window.echarts.init(this.$refs.statisticsChart);
      const years = this.statisticsData.map(d => d.year + "年");
      const option = {
        tooltip: { trigger: "axis" },
        legend: { data: ["植被覆盖率(%)", "植被成活率(%)", "土壤质量达标率(%)", "水量质量达标率(%)", "危岩体数(个)"], textStyle: { color: "#fff" } },
        grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
        xAxis: { type: "category", data: years, axisLine: { lineStyle: { color: "#fff" } }, axisLabel: { color: "#fff" } },
        yAxis: { type: "value", axisLine: { lineStyle: { color: "#fff" } }, axisLabel: { color: "#fff" }, splitLine: { lineStyle: { color: "rgba(255,255,255,0.1)" } } },
        series: [
          { name: "植被覆盖率(%)", type: "bar", data: this.statisticsData.map(d => d.vegetationCoverage) },
          { name: "植被成活率(%)", type: "bar", data: this.statisticsData.map(d => d.vegetationSurvivalRate) },
          { name: "土壤质量达标率(%)", type: "bar", data: this.statisticsData.map(d => d.soilQualityRate) },
          { name: "水量质量达标率(%)", type: "bar", data: this.statisticsData.map(d => d.waterQualityRate) },
          { name: "危岩体数(个)", type: "bar", data: this.statisticsData.map(d => d.dangerRockCount) }
        ]
      };
      chart.setOption(option);
    },
    goBack() {
      this.$router.push("/");
    },
    goHome() {
      this.$router.push("/");
    },
    openSpotInfo() {
      this.infoVisible = true;
    },
    goCompare() {
      this.$router.push({ path: "/spotCompare", query: { spotId: this.spotId } });
    },
    openMapUpload() {
      if (!this.currentStageId) {
        this.$message.warning("请先选择阶段");
        return;
      }
      this.mapUploadVisible = true;
    },
    goMonitorTask() {
      this.$router.push({ path: "/spotMonitorManage", query: { spotId: this.spotId } });
    }
  }
};
</script>

<style scoped>
.spot-detail-page {
  position: relative;
  width: 100%;
  height: calc(100vh - 60px);
  background: #0a1a2e;
  overflow: hidden;
}
.top-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50px;
  background: rgba(10, 26, 46, 0.9);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  z-index: 10;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.spot-title {
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  flex: 1;
  text-align: center;
}
.stage-controls {
  display: flex;
  align-items: center;
}
.stage-controls .el-switch__label {
  color: #fff;
}
.left-menu {
  position: absolute;
  left: 12px;
  top: 70px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.menu-item {
  width: 60px;
  height: 60px;
  background: rgba(10, 26, 46, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 12px;
}
.menu-item:hover,
.menu-item.active {
  background: rgba(64, 158, 255, 0.3);
  border-color: #409eff;
}
.menu-item i {
  font-size: 20px;
  margin-bottom: 4px;
}
.map-container {
  position: absolute;
  top: 50px;
  left: 0;
  right: 0;
  bottom: 0;
}
.bottom-chart-panel {
  position: absolute;
  bottom: 16px;
  left: 84px;
  right: 16px;
  height: 180px;
  background: rgba(10, 26, 46, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  z-index: 10;
  padding: 10px 16px;
}
.chart-title {
  color: #fff;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 8px;
}
.statistics-chart {
  width: 100%;
  height: 140px;
}
</style>



// WEBPACK FOOTER //
// src/page/spotDetail.vue